import React from 'react';

/*
 * What is actually in the SLA. This is the section that makes us a different
 * company from a barrier vendor, so it is specific: four named criteria and a
 * remedy ladder with intervals on it.
 *
 * ── On the no-numbers rule ─────────────────────────────────────────────────
 * The site bans product performance figures — no dB, no pressure drop, no
 * results chart. Nothing here breaks that. Everything on this page is a
 * SERVICE COMMITMENT: response intervals, monitoring uptime, and the bands the
 * limits are written against. Those are contractual, they are entirely within
 * our control, and they are the reason the guarantee is credible. What stays
 * out is any claim about how far our hardware moves a number.
 *
 * ── On tonality ────────────────────────────────────────────────────────────
 * The tonality clause is the one our technology is load-bearing for, and it has
 * to say NARROW-BAND. The coarse third-octave screening test smears a sharp
 * line across a wide band and understates it, which hides both the problem and
 * the fix. Don't soften "narrow-band analysis" to just "tonality" — the whole
 * commercial value of the clause is in the method.
 *
 * ── On the monitor ─────────────────────────────────────────────────────────
 * A continuous calibrated record at the compliance point reads as a liability
 * before it reads as a feature — it is evidence, and evidence cuts both ways.
 * The defense note at the bottom is not decoration; it is the answer to the
 * first objection counsel raises, and it must stay in the same eyeful as the
 * monitoring claim itself.
 */

const CRITERIA = [
  {
    n: '01',
    title: 'Level',
    body: 'Within the ordinance limit at the compliance point, measured against an agreed baseline.',
  },
  {
    n: '02',
    title: 'Tonality',
    body: 'No prominent discrete tone, by narrow-band analysis. It is what a dBA limit cannot see, and what neighbors actually react to.',
  },
  {
    n: '03',
    /* Generic on purpose (2026-08-31): the SLA is written against the standard
       third-octave bands, but enumerating them here read as a spec sheet. The
       claim that matters is the floor — how far DOWN the limits go — so say
       that and leave the band ladder to the contract. */
    title: 'Low frequency',
    body: 'Explicit limits on the low bands, down to 50 Hz, where the tone lives and where conventional treatment runs out.',
  },
  {
    n: '04',
    title: 'Availability',
    body: 'Monitoring uptime held above 98%, with the record reported to you every month.',
  },
];

const LADDER = [
  { when: '≤ 1 hour', what: 'you are alerted' },
  { when: '≤ 24 hours', what: 'root cause and attribution' },
  { when: '≤ 5 days', what: 'corrective plan' },
  { when: '≤ 30 days', what: 'remedy, at our cost' },
  { when: 'then', what: 'fee credits, and a termination right' },
];

function Guarantee() {
  return (
    <section className="section guarantee" id="guarantee">
      <div className="wrap">
        <p className="eyebrow">The guarantee</p>
        <h2 className="h2 guarantee-title">What the SLA actually commits to.</h2>
        <p className="lead guarantee-lead">
          Four criteria, written against your ordinance and an agreed baseline,
          plus a defined answer if any of them is missed. Two of the four are
          about the tone.
        </p>

        <div className="guarantee-inner">
          <div className="guarantee-criteria">
            {CRITERIA.map((c) => (
              <div className="guarantee-criterion" key={c.n}>
                <span className="guarantee-criterion-n">{c.n}</span>
                <div>
                  <h3 className="guarantee-criterion-title">{c.title}</h3>
                  <p className="guarantee-criterion-body">{c.body}</p>
                </div>
              </div>
            ))}

            {/* Why the tonality clause is worth arguing for: the rules are
                already moving this way, and a plain dBA limit is the one case
                where none of this is worth much to anyone. */}
            <p className="guarantee-note">
              Ordinances are hardening past dBA. Prince William County passed a
              low-frequency ordinance in 2025, and UK practice has penalised
              tonality for a decade. BS 4142 adds up to +6 dB for a prominent
              tone.
            </p>
          </div>

          <aside className="guarantee-remedy">
            <h3 className="h3 guarantee-remedy-title">If it is exceeded</h3>
            <ol className="guarantee-ladder">
              {LADDER.map((l) => (
                <li className="guarantee-rung" key={l.when}>
                  <span className="guarantee-rung-when">{l.when}</span>
                  <span className="guarantee-rung-what">{l.what}</span>
                </li>
              ))}
            </ol>
          </aside>
        </div>

        <div className="guarantee-defense">
          <h3 className="guarantee-defense-title">
            The measurement is also your defense.
          </h3>
          <p className="guarantee-defense-body">
            Today a neighbor says it got louder since you arrived, and you have
            nothing to say back. No baseline, no record, no way to separate
            your plant from the road. A continuous, calibrated, timestamped
            record at the compliance point is the exhibit you currently
            don&rsquo;t have. The data is yours.
          </p>
        </div>

        {/* Hand-off into the silencer section, and the hinge of the whole
            page. Level and availability are a screen and a monitor — bought in,
            unremarkable. Tonality and the low-frequency bands are the two
            clauses nothing on the market can hold, which is the entire reason
            there is a technology here at all. Without this line the reader
            drops from contract language straight into physics with nothing
            connecting them. */}
        <p className="guarantee-handoff">
          <strong className="guarantee-handoff-lead">
            Level and availability are a screen and a monitor.
          </strong>{' '}
          Bought in, well understood, and not where the difficulty is. Tonality
          and the low-frequency bands are the two clauses nothing on the market
          can hold. A chiller has to breathe, so the opening stays open and
          the tone goes over the wall, and it moves with fan speed, so whatever
          holds it has to move too. Closing that gap took a new kind of
          element, one that tracks the tone, and it is the one thing here we
          invented ourselves.
        </p>
      </div>
    </section>
  );
}

export default Guarantee;
